const MENTION_TOKEN = /@@([\w\s]+?)@@/g;
const QUERY_CHAR = /[\w ]/;

export function findMentionTokens(text) {
  const tokens = [];
  const regex = new RegExp(MENTION_TOKEN.source, "g");
  let match;

  while ((match = regex.exec(text)) !== null) {
    tokens.push({
      name: match[1],
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  return tokens;
}

function overlapsToken(tokens, start, end) {
  return tokens.some((token) => start < token.end && end > token.start);
}

export function getActiveMention(text, caret) {
  if (typeof text !== "string") return null;

  const safeCaret = Math.max(0, Math.min(caret ?? text.length, text.length));
  const tokens = findMentionTokens(text);
  const insideToken = tokens.some(
    (token) => safeCaret > token.start && safeCaret < token.end,
  );
  if (insideToken) return null;

  let queryStart = safeCaret;
  while (queryStart > 0 && QUERY_CHAR.test(text[queryStart - 1])) {
    queryStart -= 1;
  }

  if (queryStart === 0 || text[queryStart - 1] !== "@") return null;

  let triggerStart = queryStart - 1;
  while (triggerStart > 0 && text[triggerStart - 1] === "@") {
    triggerStart -= 1;
  }

  if (overlapsToken(tokens, triggerStart, queryStart)) return null;

  const query = text.slice(queryStart, safeCaret);
  if (/^\s/.test(query)) return null;

  return {
    start: triggerStart,
    end: safeCaret,
    query,
  };
}

export function insertMention(text, mention, name) {
  const token = `@@${name}@@`;
  const after = text.slice(mention.end);
  const needsSpace = after.length === 0 || !/^\s/.test(after);
  const insertion = needsSpace ? `${token} ` : token;
  const nextText = text.slice(0, mention.start) + insertion + after;

  return {
    text: nextText,
    caret: mention.start + insertion.length,
  };
}

export function getMentionTokenBeforeCaret(text, caret) {
  const safeCaret = caret ?? 0;
  return (
    findMentionTokens(text).find((token) => token.end === safeCaret) || null
  );
}

export function deleteMentionToken(text, token) {
  let end = token.end;
  if (text[end] === " ") end += 1;

  return {
    text: text.slice(0, token.start) + text.slice(end),
    caret: token.start,
  };
}

export function syncMentionData(text, selectedMentionData) {
  const names = findMentionTokens(text).map((token) => token.name);
  return (selectedMentionData || []).filter((mention) =>
    names.includes(mention.name),
  );
}

export function splitMentionHighlights(text) {
  const source = text || "";
  const parts = [];
  const tokens = findMentionTokens(source);
  let cursor = 0;

  tokens.forEach((token) => {
    if (token.start > cursor) {
      parts.push({
        text: source.slice(cursor, token.start),
        mention: false,
      });
    }
    parts.push({
      text: source.slice(token.start, token.end),
      mention: true,
    });
    cursor = token.end;
  });

  if (cursor < source.length) {
    parts.push({ text: source.slice(cursor), mention: false });
  }

  return parts;
}

function normalize(value) {
  return (value || "").trim().toLowerCase();
}

export function buildMentionOptions({
  members = [],
  query = "",
  includeAiCommand = false,
  onlineUserIds = new Set(),
} = {}) {
  const specials = [
    {
      id: "all",
      name: "All",
      subtitle: "Notifies everyone",
      kind: "all",
      pinned: true,
    },
    {
      id: "ai",
      name: "Tiger ZERO AI",
      subtitle: "AI assistant",
      kind: "ai",
      pinned: true,
    },
  ];

  if (includeAiCommand) {
    specials.push({
      id: "ai-cmd",
      name: "Tiger ZERO AI Command",
      subtitle: "Command this AI",
      kind: "ai-command",
      pinned: true,
    });
  }

  const people = [];
  (Array.isArray(members) ? members : []).forEach((member) => {
    const user = member?.user;
    if (!user?.id || !user.display_name) return;
    people.push({
      id: user.id,
      name: user.display_name,
      subtitle: user.email_address || "",
      kind: "person",
      pinned: false,
      profilePhoto: user.profile_photo,
      isOnline: onlineUserIds.has(user.id),
    });
  });

  const normalizedQuery = normalize(query);
  const matches = (option) =>
    !normalizedQuery || option.name.toLowerCase().includes(normalizedQuery);

  const matchedPeople = people.filter(matches).sort((a, b) => {
    if (!normalizedQuery) return a.name.localeCompare(b.name);
    const aStarts = a.name.toLowerCase().startsWith(normalizedQuery) ? 0 : 1;
    const bStarts = b.name.toLowerCase().startsWith(normalizedQuery) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    return a.name.localeCompare(b.name);
  });

  return [...specials.filter(matches), ...matchedPeople];
}
